import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Grid, Paper, Typography } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LayersIcon from '@mui/icons-material/Layers';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PublicIcon from '@mui/icons-material/Public';
import DescriptionIcon from '@mui/icons-material/Description';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import ArticleIcon from '@mui/icons-material/Article';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ApiIcon from '@mui/icons-material/Api';
import { PagePathname } from '../../../routes/paths';
import { getCurrentUser } from '../../../context/CommunicationProvider/authUser';

// CONTRACT: TILES hat GENAU 12 Basis-Eintraege — eine Kachel pro Einstellungs-Seite (AK-2).
// CONTRACT: Jede dieser Kacheln verweist auf eine Seite, die EinstellungenSeitenKopf traegt.
// Zaehlpruefung: EinstellungenUebersichtPage.test.tsx (R-1/AK-2 — 12 Kacheln im DOM ohne
// SUPERADMIN). Cross-Referenz: pages-title-matrix.test.tsx (PAGE_TITLE_MATRIX Zaehl-Lock, 12).
// HINWEIS (Epic-Merge RITA-1314): Die rollenabhaengige MCP-Kachel steht bewusst NICHT in
// dieser Liste — sie traegt keinen EinstellungenSeitenKopf und ist deshalb auch nicht Teil
// der PAGE_TITLE_MATRIX. Siehe MCP_TILE weiter unten.
const TILES = [
  { label: 'Buchungskonten', icon: AccountBalanceIcon, to: PagePathname.buchungskonten },
  { label: 'Ränge', icon: EmojiEventsIcon, to: PagePathname.ränge },
  { label: 'Level', icon: LayersIcon, to: PagePathname.levels },
  { label: 'Benutzer', icon: PeopleIcon, to: PagePathname.benutzer },
  { label: 'Marken', icon: BusinessIcon, to: PagePathname.marken },
  { label: 'Leistungen', icon: BuildIcon, to: PagePathname.leistungen },
  { label: 'Steuern', icon: ReceiptIcon, to: PagePathname.steuern },
  { label: 'Länder', icon: PublicIcon, to: PagePathname.länder },
  { label: 'Vertragsarten', icon: DescriptionIcon, to: PagePathname.vertragsarten },
  { label: 'Systemdienstleister', icon: DeviceHubIcon, to: PagePathname.systemdienstleister },
  { label: 'Vorlagen', icon: ArticleIcon, to: PagePathname.briefvorlagen },
  { label: 'Info', icon: AdminPanelSettingsIcon, to: PagePathname.adminPanel },
];

// CONTRACT (RITA-1370, uebernommen aus der frueheren Nav-Untermenue-Loesung beim
// Epic-Merge RITA-1314): Die MCP-Kachel wird AUSSCHLIESSLICH fuer SUPERADMIN-Nutzer
// gerendert — sie ist damit die 13. Kachel, aber nur fuer diese Rolle.
// Die Auswertung erfolgt zur RENDER-Zeit (nicht Module-Load-Zeit), damit der
// JWT-State aus localStorage bei jedem Render aktuell ist.
// Zaehlpruefung: EinstellungenUebersichtPage.test.tsx — 12 Kacheln ohne SUPERADMIN,
// 13 mit. Das Backend-Scope-Gate bleibt die autoritative Grenze; diese Kachel steuert
// nur die Sichtbarkeit.
const MCP_TILE = { label: 'MCP', icon: ApiIcon, to: PagePathname.mcpAdministration };

function useVisibleTiles() {
  const user = getCurrentUser();
  const isSuperAdmin = Boolean(
    user?.roles
      ?.split(',')
      .map((r) => r.trim())
      .includes('SUPERADMIN'),
  );
  return isSuperAdmin ? [...TILES, MCP_TILE] : TILES;
}

function EinstellungenUebersichtPage() {
  const tiles = useVisibleTiles();
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Einstellungen
      </Typography>
      <Grid container spacing={2} data-testid="einstellungen-uebersicht-kacheln">
        {tiles.map(({ label, icon: Icon, to }) => (
          <Grid item xs={6} sm={4} md={3} key={label}>
            <Link to={to} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  color: 'text.primary',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease',
                  '&:hover': {
                    boxShadow: 4,
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Icon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Typography variant="body1" fontWeight={400} textAlign="center">
                  {label}
                </Typography>
              </Paper>
            </Link>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default EinstellungenUebersichtPage;
